import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowDown,
    faArrowUp,
    faRedo,
} from '@fortawesome/free-solid-svg-icons';
import { getLogs } from '../../services/LogsService';
import { parseLogs } from './logParser';
import CopyButton from '../CopyButton/CopyButton';
import Pager from '../Pager/Pager';
import './LogView.css';

const NO_LOG_YET = 'No log yet.';
const PAGE_SIZE = 50;

const LEVEL_BADGES = {
    info: 'bg-info text-dark',
    warn: 'bg-warning text-dark',
    error: 'bg-danger',
};

function asText(data) {
    return typeof data === 'string' ? data : JSON.stringify(data);
}

function errorText(err) {
    if (err.response && err.response.status === 404) return NO_LOG_YET;
    return asText(err.response ? err.response.data : err.message);
}

function badgeClass(level) {
    return 'badge ' + (LEVEL_BADGES[level] || 'bg-secondary');
}

/**
 * props:
 * - file
 */
function LogView(props) {
    const [logs, setLogs] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [levelFilter, setLevelFilter] = useState('ALL');
    const [newestFirst, setNewestFirst] = useState(true);
    const [page, setPage] = useState(1);
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        setLogs('');
        setError('');
        if (!props.file) return undefined;

        let isCurrent = true;
        setIsLoading(true);
        getLogs(props.file)
            .then(text => {
                if (isCurrent) setLogs(asText(text));
            })
            .catch(err => {
                if (isCurrent) setError(errorText(err));
            })
            .finally(() => {
                if (isCurrent) setIsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [props.file, refreshTick]);

    useEffect(() => {
        setPage(1);
    }, [props.file, search, levelFilter, newestFirst, refreshTick]);

    const entries = useMemo(() => parseLogs(logs), [logs]);

    const filteredEntries = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return entries.filter(entry => {
            if (
                levelFilter !== 'ALL' &&
                entry.level !== levelFilter.toLowerCase()
            )
                return false;
            if (needle && !entry.message.toLowerCase().includes(needle))
                return false;
            return true;
        });
    }, [entries, search, levelFilter]);

    const sortedEntries = useMemo(() => {
        const indexed = filteredEntries.map((entry, index) => ({
            entry,
            index,
        }));
        indexed.sort((a, b) => {
            const timeA = a.entry.timestamp
                ? a.entry.timestamp.getTime()
                : null;
            const timeB = b.entry.timestamp
                ? b.entry.timestamp.getTime()
                : null;
            if (timeA !== null && timeB !== null && timeA !== timeB)
                return newestFirst ? timeB - timeA : timeA - timeB;
            return newestFirst ? b.index - a.index : a.index - b.index;
        });
        return indexed.map(item => item.entry);
    }, [filteredEntries, newestFirst]);

    const pagesQty = Math.ceil(sortedEntries.length / PAGE_SIZE);
    const currentPage = Math.min(page, pagesQty || 1);
    const pageEntries = sortedEntries.slice(
        (currentPage - 1) * PAGE_SIZE,
        currentPage * PAGE_SIZE,
    );

    const showError = error && error !== NO_LOG_YET;
    let body;
    if (showError) {
        body = <div className="alert alert-danger py-2">{error}</div>;
    } else if (isLoading) {
        body = <div className="text-muted small">Loading...</div>;
    } else if (!props.file || error === NO_LOG_YET || logs === '') {
        body = <div className="text-muted py-4 text-center">{NO_LOG_YET}</div>;
    } else {
        body = (
            <>
                <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="border-gray-200 log-sort-header"
                                    role="button"
                                    onClick={() =>
                                        setNewestFirst(first => !first)
                                    }
                                    title="Toggle the sort order"
                                >
                                    Date{' '}
                                    <FontAwesomeIcon
                                        icon={
                                            newestFirst
                                                ? faArrowDown
                                                : faArrowUp
                                        }
                                        className="icon icon-xs"
                                    />
                                </th>
                                <th className="border-gray-200">Level</th>
                                <th className="border-gray-200">Message</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageEntries.map((entry, index) => (
                                <tr key={(currentPage - 1) * PAGE_SIZE + index}>
                                    <td className="text-nowrap text-gray-600 small">
                                        {entry.timestamp
                                            ? entry.timestamp.toLocaleString()
                                            : '—'}
                                    </td>
                                    <td>
                                        <span
                                            className={badgeClass(entry.level)}
                                        >
                                            {entry.level}
                                        </span>
                                    </td>
                                    <td className="log-message text-gray-800">
                                        {entry.message}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pager
                    count={sortedEntries.length}
                    page={currentPage}
                    size={PAGE_SIZE}
                    onClick={setPage}
                />
            </>
        );
    }

    return (
        <div className="log-view">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <input
                    type="search"
                    className="form-control form-control-sm log-search"
                    placeholder="Search messages..."
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                />
                <select
                    className="form-select form-select-sm log-level-filter"
                    value={levelFilter}
                    onChange={event => setLevelFilter(event.target.value)}
                    title="Filter by level"
                >
                    <option value="ALL">All levels</option>
                    <option value="INFO">Info</option>
                    <option value="WARN">Warn</option>
                    <option value="ERROR">Error</option>
                </select>
                <button
                    type="button"
                    className="btn btn-gray-800 btn-sm d-inline-flex align-items-center"
                    onClick={() => setRefreshTick(tick => tick + 1)}
                    disabled={isLoading || !props.file}
                    title="Reload the logs"
                >
                    <FontAwesomeIcon
                        icon={faRedo}
                        className={
                            'icon icon-xs me-1' + (isLoading ? ' fa-spin' : '')
                        }
                    />
                    Refresh
                </button>
                <CopyButton text={logs} />
            </div>
            {body}
        </div>
    );
}

export default LogView;
